import { Callout, PlaybackValue } from '@piggybank/core';
import { Field, Select } from '@piggybank/form';
import { connect } from 'formik';
import get from 'lodash/get';
import isPlainObject from 'lodash/isPlainObject';
import last from 'lodash/last';
import mapValues from 'lodash/mapValues';
import memoize from 'lodash/memoize';
import T from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { DynamicFormFieldFeedback } from '../../DynamicFormFieldFeedback';
import { DynamicFormHint } from '../../DynamicFormHint';
import { DynamicFormLabel } from '../../DynamicFormLabel';
import { renderMarkdown } from '../../Markdown/renderMarkdown';
import {
  Optional,
  Translation,
  TranslationScope,
  useProductCodes,
} from '../../context';
import { transformEnumsAtSchemaNode } from '../../operators';
import {
  formNameFromKeyPath,
  localizationKeyPath,
  requiredArg,
} from '../../utils';

const memoizeEnums = memoize(
  ({ schemaNode, index, inputName, values = [] }) => {
    const containsProperties =
      !!values.length && values.every(value => isPlainObject(value));

    return transformEnumsAtSchemaNode(schemaNode).filter(enumeration => {
      if (containsProperties) {
        return (
          get(values, [index, inputName].join('.')) === enumeration ||
          !values.some(obj => get(obj, inputName, null) === enumeration)
        );
      }

      return values[index] == enumeration || !values.includes(enumeration);
    });
  },
  ({ fieldName, index, values }) => JSON.stringify({ index, values, fieldName })
);

export const DedupeSelectField = ({
  formValues,
  formik,
  schemaNode,
  context,
  setMetadataForFieldName,
  getPageLevelErrors = requiredArg('getPageLevelErrors'),
}) => {
  const { groupFieldName, index } = context.group;
  const groupValues = get(formValues, groupFieldName, []);
  const fieldName = formNameFromKeyPath(context.keyPath).join('.');
  const inputName = last(fieldName.split('.'));
  const readOnly = get(formValues, `readOnly.${fieldName}`, []).length > 0;
  const productCodes = useProductCodes();

  useEffect(() => {
    if (
      typeof get(formValues, fieldName) === 'undefined' &&
      typeof get(formik, 'setFieldValue') === 'function'
    ) {
      formik.setFieldValue(
        fieldName,
        index === 0 ? schemaNode.default || '' : ''
      );
    }
  }, [get(formValues, fieldName)]);

  const dynamicFormFieldFeedbackProps = {
    fieldName,
    errors: getPageLevelErrors(),
  };

  const currentValue = useMemo(
    () =>
      get(
        groupValues,
        [index, ...(inputName !== `${index}` ? [inputName] : [])].join('.')
      ),
    [groupValues, index]
  );

  return (
    <TranslationScope path={localizationKeyPath(context.keyPath)}>
      <Translation key={fieldName} path=".">
        {({ optionLabels }) => {
          const textLabels = mapValues(optionLabels, optionLabel =>
            get(optionLabel, 'label', optionLabel)
          );

          const labelCallout = get(optionLabels, [currentValue, 'callout']);

          setMetadataForFieldName(fieldName, {
            componentName: 'Select',
            componentProps: {
              options: textLabels,
            },
            context: {
              ...context,
              parent: '@@',
            },
          });

          return (
            <Field name={fieldName}>
              <DynamicFormLabel
                context={context}
                formValues={formValues}
                schemaNode={schemaNode}
              />
              {readOnly ? (
                <PlaybackValue>
                  {get(
                    textLabels,
                    groupValues[index],
                    get(textLabels, groupValues[index].type)
                  )}
                </PlaybackValue>
              ) : (
                <>
                  <Optional>
                    <DynamicFormHint
                      context={context}
                      formValues={formValues}
                    />
                  </Optional>
                  <Select
                    marginBottom={labelCallout ? 2 : 0}
                    options={memoizeEnums({
                      ...(inputName != index ? { inputName } : {}),
                      schemaNode,
                      index: String(index),
                      values: groupValues,
                      fieldName,
                    }).map(value => ({
                      value,
                      label: get(textLabels, value, value),
                    }))}
                  />
                  {labelCallout && (
                    <Callout marginBottom={0} type={labelCallout.type}>
                      {renderMarkdown(
                        labelCallout.content,
                        {
                          ...formValues,
                          ...productCodes,
                        },
                        {
                          translationPath: [
                            ...localizationKeyPath(context.keyPath),
                            currentValue,
                            'callout',
                          ],
                        }
                      )}
                    </Callout>
                  )}
                  <DynamicFormFieldFeedback
                    {...dynamicFormFieldFeedbackProps}
                  />
                </>
              )}
            </Field>
          );
        }}
      </Translation>
    </TranslationScope>
  );
};

DedupeSelectField.propTypes = {
  fieldName: T.string,
  context: T.shape({
    keyPath: T.object,
    schemaPath: T.arrayOf(T.oneOfType([T.string, T.number])),
    group: T.shape({
      index: T.number,
      groupFieldName: T.arrayOf(T.string),
    }),
  }),
  formik: T.shape({
    setFieldValue: T.func,
  }),
  formValues: T.object,
  groupValues: T.array,
  index: T.number,
  schemaNode: T.object,
  setMetadataForFieldName: T.func,
  getPageLevelErrors: T.func,
};

export default connect(DedupeSelectField);
